import { Button, Icon, IconName } from "@/shared/components";
import { cn } from "@/shared/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { ReactNode } from "react";

const messageBarVariants = cva("flex justify-between items-center border-l-4 px-6 py-3", {
    variants: {
        variant: {
            success: "bg-success-subtle border-success",
            info: "bg-info-subtle border-info",
            warning: "bg-warning-subtle border-warning",
            error: "bg-error-subtle border-error",
        },
    },
    defaultVariants: {
        variant: "info",
    },
});

const iconVariants = cva("", {
    variants: {
        variant: {
            success: "text-content-success",
            info: "text-content-info",
            warning: "text-content-warning",
            error: "text-content-error",
        },
    },
    defaultVariants: {
        variant: "info",
    },
});

type MessageVariants = VariantProps<typeof messageBarVariants>;

const iconMap: Record<NonNullable<MessageVariants["variant"]>, IconName> = {
    success: "check-circle-filled",
    info: "info-filled",
    warning: "alert-filled",
    error: "error-filled",
};

type DefaultCloseButtonProps = {
    onClose?: () => void;
};

type CustomCloseButtonProps = {
    closeButton?: ReactNode;
};

type MessageBarProps = MessageVariants &
    (DefaultCloseButtonProps | CustomCloseButtonProps) & {
        children: ReactNode;
        className?: string;
    };

export function MessageBar({ children, variant = "info", className, ...props }: MessageBarProps) {
    const onClose = "onClose" in props ? props.onClose : null;
    const closeButton = "closeButton" in props ? props.closeButton : null;

    return (
        <div className={cn(messageBarVariants({ variant }), className)} data-testid={`message-bar-${variant}`}>
            <div className="flex gap-3">
                <Icon className={iconVariants({ variant })} name={iconMap[variant!]} />
                <p>{children}</p>
            </div>
            {closeButton ? closeButton : onClose ? <Button variant="tertiary" iconOnly icon="close" onClick={onClose} /> : null}
        </div>
    );
}

type MessageBarGezienProps = MessageVariants &
    DefaultCloseButtonProps & {
        children: ReactNode;
        className?: string;
        label?: string;
    };

export function MessageBarGezien({ children, variant = "info", className, onClose, label = "Gezien" }: MessageBarGezienProps) {
    return (
        <div className={cn(messageBarVariants({ variant }), className)} data-testid="message-bar-gezien">
            <div className="flex gap-3">
                <Icon className={iconVariants({ variant })} name={iconMap[variant!]} />
                <p>{children}</p>
            </div>
            <Button variant="tertiary" onClick={onClose} size="s">
                {label}
            </Button>
        </div>
    );
}
